/**
 * Deep-link login: nhớ URL đích khi người dùng bấm vào chức năng cần đăng nhập,
 * để sau khi đăng nhập điều hướng trở lại đúng trang đó (kèm query & hash).
 *
 * Nguyên tắc an toàn: CHỈ chấp nhận đường dẫn nội bộ (bắt đầu bằng '/'),
 * từ chối URL tuyệt đối / có scheme / dạng '//host' để tránh open-redirect.
 */

const RETURN_URL_KEY = 'eduapp_return_url';
const LOGIN_PATH = '/dang-nhap';

/** Kiểm tra một chuỗi có phải đường dẫn nội bộ an toàn để điều hướng tới hay không. */
export const isSafeInternalPath = (url: string | null | undefined): url is string => {
  if (!url || typeof url !== 'string') return false;
  if (!url.startsWith('/')) return false;       // phải là path gốc tương đối
  if (url.startsWith('//')) return false;        // chặn //evil.com
  if (url.startsWith('/\\')) return false;       // chặn /\evil.com (một số trình duyệt hiểu là //)
  if (url.startsWith(LOGIN_PATH)) return false;  // tránh vòng lặp quay lại trang đăng nhập
  return true;
};

/** Lưu URL đích vào sessionStorage (sống sót qua social-login round-trip / refresh). */
export const saveReturnUrl = (url: string): void => {
  try {
    if (isSafeInternalPath(url)) sessionStorage.setItem(RETURN_URL_KEY, url);
  } catch {
    /* sessionStorage có thể bị chặn (private mode) — bỏ qua */
  }
};

/**
 * Đọc & xoá URL đích. Ưu tiên tham số `?returnUrl=` trên URL hiện tại,
 * fallback về sessionStorage. Trả về null nếu không có / không an toàn.
 */
export const consumeReturnUrl = (): string | null => {
  let candidate: string | null = null;
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('returnUrl');
    const decodedQuery = fromQuery ? safeDecode(fromQuery) : null;
    const fromStore = sessionStorage.getItem(RETURN_URL_KEY);

    if (isSafeInternalPath(decodedQuery)) candidate = decodedQuery;
    else if (isSafeInternalPath(fromStore)) candidate = fromStore;

    sessionStorage.removeItem(RETURN_URL_KEY);
  } catch {
    candidate = null;
  }
  return candidate;
};

/** Dựng URL trang đăng nhập kèm returnUrl đã encode; nếu target không hợp lệ thì trả về trang login trơn. */
export const buildLoginUrl = (targetUrl: string): string =>
  isSafeInternalPath(targetUrl)
    ? `${LOGIN_PATH}?returnUrl=${encodeURIComponent(targetUrl)}`
    : LOGIN_PATH;

const safeDecode = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};
